import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Zoom,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { baseUrl, endpoints } from 'Api';
import { V2ThemeProvider } from 'Pages/private/v2/_shared/V2Layout';
import { useNewUI } from 'hooks/useNewUI';
import { beginUiSwitch } from 'Utils/uiSwitchOverlay';

// One-time invitation to try the new UI. It only appears when the backend opts
// in (`show_new_ui_popup`) for a signed-in user who is on the classic layout
// while switching is allowed (`available: 'both'`, `default_ui: 'legacy'`).
// When the API default is the new UI, `useNewUI().enabled` is already true, so
// this never shows (the "skip when new is default" rule). Dismissing it (either
// button) is permanent for this browser.
const DISMISSED_KEY = 'newUiInviteDismissed';
const SHOW_DELAY_MS = 900;

const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return true;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    /* storage blocked — the dialog is already closing for this session */
  }
};

const hasAuthToken = () => {
  try {
    const t = localStorage.getItem('token');
    return !!t && t !== 'undefined' && t !== 'null';
  } catch {
    return false;
  }
};

// Best-effort funnel logging for the invite popup. `event`: 'shown' |
// 'dismissed' | 'accepted'. text/plain keeps it preflight-free; keepalive lets
// 'accepted' finish after beginUiSwitch reloads the page. Never throws.
const trackPopupEvent = (event) => {
  try {
    const token = localStorage.getItem('token');
    if (!token || token === 'undefined' || token === 'null') return;
    fetch(`${baseUrl}${endpoints.trackPopupEvent}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ token, event }),
    }).catch(() => { /* best-effort telemetry */ });
  } catch (e) { /* noop */ }
};

const FEATURES = [
  { icon: BoltRoundedIcon, title: 'Faster & cleaner', desc: 'A lighter layout that gets out of your way.' },
  { icon: DarkModeRoundedIcon, title: 'Dark mode', desc: 'Easy on the eyes, day or night.' },
  { icon: DashboardCustomizeRoundedIcon, title: 'Same content, refreshed', desc: 'Everything you have, redesigned.' },
];

// A tiny abstract mock of the new layout — pure CSS, no assets. Sits in the
// gradient hero to give the invite a sense of what's on the other side.
const PreviewMock = () => (
  <Box
    sx={{
      width: 230,
      borderRadius: 2.5,
      bgcolor: 'rgba(255,255,255,0.96)',
      boxShadow: '0 18px 40px rgba(12,32,63,0.28)',
      p: 1.25,
      transform: 'rotate(-2deg)',
    }}
  >
    <Stack direction="row" spacing={0.6} sx={{ mb: 1 }}>
      {['#ff6058', '#ffbd2e', '#28c840'].map((c) => (
        <Box key={c} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c }} />
      ))}
    </Stack>
    <Stack direction="row" spacing={1}>
      <Stack spacing={0.6} sx={{ width: 42, flexShrink: 0 }}>
        <Box sx={{ height: 8, borderRadius: 1, bgcolor: '#5aa5ef' }} />
        {[0.5, 0.35, 0.35, 0.5].map((w, i) => (
          <Box key={i} sx={{ height: 6, width: `${w * 100}%`, borderRadius: 1, bgcolor: 'rgba(15,23,42,0.12)' }} />
        ))}
      </Stack>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                height: 30,
                borderRadius: 1.25,
                background: i % 3 === 0
                  ? 'linear-gradient(135deg, rgba(90,165,239,0.25), rgba(90,165,239,0.08))'
                  : 'rgba(15,23,42,0.06)',
              }}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  </Box>
);

const NewUISwitchPrompt = () => {
  const { enabled, canSwitch, showPopup } = useNewUI();
  const [dismissed, setDismissed] = useState(readDismissed());
  const [ready, setReady] = useState(false);
  const shownLoggedRef = useRef(false);

  // Eligible only when the backend asks for the popup (`showPopup`) and the user
  // is signed in, on classic UI, allowed to switch, and hasn't dismissed before.
  // `!enabled` is what skips the prompt when the API default is already the new UI.
  const eligible = showPopup && canSwitch && !enabled && !dismissed && hasAuthToken();

  // Hold back a beat so the dialog doesn't slam in during the first paint / the
  // getConfig round-trip that flips `canSwitch`.
  useEffect(() => {
    if (!eligible) {
      setReady(false);
      return undefined;
    }
    const t = setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [eligible]);

  // Log the impression once, when the dialog actually becomes visible.
  useEffect(() => {
    if (eligible && ready && !shownLoggedRef.current) {
      shownLoggedRef.current = true;
      trackPopupEvent('shown');
    }
  }, [eligible, ready]);

  const close = () => {
    trackPopupEvent('dismissed'); // "Maybe later" / X / backdrop
    markDismissed();
    setDismissed(true);
  };

  const handleSwitch = () => {
    // No 'accepted' event here — beginUiSwitch logs a switch row
    // (source='popup', to_ui='new'), which the funnel counts as the conversion.
    markDismissed();
    setDismissed(true);
    beginUiSwitch(true, 'popup'); // saves the pick, shows the switch overlay, reloads
  };

  if (!eligible || !ready) return null;

  return (
    <V2ThemeProvider withBaseline={false}>
      <Dialog
        open
        onClose={close}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      >
        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            px: 3,
            pt: 3.5,
            pb: 3,
            background: 'linear-gradient(135deg, #5aa5ef 0%, #4f7fe0 55%, #6b5cf0 100%)',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -60,
              right: -40,
              width: 160,
              height: 160,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.14)',
            }}
          />
          <IconButton
            aria-label="Close"
            onClick={close}
            disableRipple
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              // Explicit square + 50% radius so the classic app's global
              // `button` styles can't squash it into an egg shape.
              width: 30,
              height: 30,
              minWidth: 30,
              padding: 0,
              borderRadius: '50%',
              flex: '0 0 auto',
              color: 'rgba(255,255,255,0.95)',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <PreviewMock />
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Chip
                icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 15, color: '#fff !important' }} />}
                label="New experience"
                size="small"
                sx={{
                  height: 24,
                  mb: 1.25,
                  px: 0.5,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #5aa5ef, #6b5cf0)',
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
                Say hello to a fresh new look
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                We&apos;ve rebuilt Projector to be faster and cleaner. Give it a
                try — you can switch back to the classic layout any time from Settings.
              </Typography>
            </Box>

            <Stack spacing={1.75}>
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <Stack key={title} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#5aa5ef',
                      bgcolor: 'rgba(90,165,239,0.12)',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {desc}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>

            <Stack spacing={1}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSwitch}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  py: 1.25,
                  fontSize: 14.5,
                  fontWeight: 700,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #5aa5ef, #5a6ff0)',
                  '&:hover': { background: 'linear-gradient(135deg, #4f97e6, #4f63e6)' },
                }}
              >
                Switch to the new experience
              </Button>
              <Button
                variant="text"
                fullWidth
                onClick={close}
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                Maybe later
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </V2ThemeProvider>
  );
};

export default NewUISwitchPrompt;
